import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Sparkles, Send, X, Loader2, Search, Mail, Check, AlertTriangle,
  Upload, FileText, FlaskConical, Trash2, RotateCcw,
} from 'lucide-react';
import SpamCheckChip from '../components/SpamCheckChip';

const API = '/api';
const STORAGE_KEY = 'followup_composer_state_v2';

const DEFAULT_PROMPT =
  'Warm follow-up after meeting at BIO China conference. Thank them for the conversation, ' +
  'recap 1-2 specific points from the minutes, and propose a clear next step (deck share / call / NDA).';

function makeRow() {
  return {
    rowId: `r_${Math.random().toString(36).slice(2, 9)}`,
    company: null,
    contact: null,
    ccEmails: [],
    momText: '',
    draftSubject: '',
    draftBody: '',
    status: 'idle', // idle | generating | drafted | sending | sent | error | testing | tested
    error: '',
    routingOverride: false,
  };
}

// localStorage helpers — only persist what we'd want back after a refresh.
const persistState = (rows, userPrompt, uploadSummary) => {
  try {
    const slim = rows.map(r => ({
      rowId: r.rowId,
      company: r.company,
      contact: r.contact,
      ccEmails: r.ccEmails || [],
      momText: r.momText || '',
      draftSubject: r.draftSubject || '',
      draftBody: r.draftBody || '',
      status: r.status === 'generating' || r.status === 'sending' || r.status === 'testing'
        ? (r.draftSubject ? 'drafted' : 'idle')
        : r.status,
      meetingMeta: r.meetingMeta,
      routingOverride: !!r.routingOverride,
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      rows: slim, userPrompt, uploadSummary, savedAt: Date.now(),
    }));
  } catch { /* quota / private mode — ignore */ }
};

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rows?.length) return null;
    return parsed;
  } catch { return null; }
};

export default function FollowUpComposerPage() {
  const initial = loadState();
  const [userPrompt, setUserPrompt] = useState(initial?.userPrompt || DEFAULT_PROMPT);
  const [rows, setRows] = useState(initial?.rows?.length ? initial.rows : [makeRow()]);
  const [uploadingSchedule, setUploadingSchedule] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(initial?.uploadSummary || null);
  const fileInputRef = useRef(null);

  // Persist on every state change so a refresh restores work.
  useEffect(() => {
    persistState(rows, userPrompt, uploadSummary);
  }, [rows, userPrompt, uploadSummary]);

  const updateRow = (rowId, patch) =>
    setRows(prev => prev.map(r => (r.rowId === rowId ? { ...r, ...patch } : r)));
  const removeRow = (rowId) =>
    setRows(prev => (prev.length === 1 ? prev : prev.filter(r => r.rowId !== rowId)));
  const addRow = () => setRows(prev => [...prev, makeRow()]);

  const clearAll = () => {
    if (!window.confirm('Clear the entire schedule and start over? This wipes all parsed meetings, drafts, and minutes.')) return;
    localStorage.removeItem(STORAGE_KEY);
    setRows([makeRow()]);
    setUploadSummary(null);
    setUserPrompt(DEFAULT_PROMPT);
    toast.success('Cleared.');
  };

  const handleScheduleUpload = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.pdf') && !lower.endsWith('.xlsx') && !lower.endsWith('.xls') && !lower.endsWith('.xlsm')) {
      toast.error('Please upload a .pdf or .xlsx file');
      return;
    }
    setUploadingSchedule(true);
    setUploadSummary(null);
    try {
      let token = null;
      try {
        const t = await axios.get(`${API}/auth/token`, { withCredentials: true });
        token = t.data?.session_token || null;
      } catch { /* fall through — endpoint may 401 */ }

      const fd = new FormData();
      fd.append('file', file);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/follow-up/parse-schedule`, fd, { headers });
      const newRows = (data.rows || []).map(r => ({
        ...makeRow(),
        company: r.company,
        contact: r.contact && (r.contact.email || r.contact.name) ? r.contact : null,
        meetingMeta: { time: r.meeting_time, location: r.location, matchStatus: r.match_status },
      }));
      if (newRows.length === 0) {
        toast.error('No meetings detected in the file');
      } else {
        setRows(newRows);
        setUploadSummary({
          total: data.total_parsed || newRows.length,
          counts: data.match_counts || {},
          fileName: file.name,
        });
        toast.success(`Parsed ${newRows.length} meetings from ${file.name}`);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 401) msg = 'Session expired — please log out and log back in';
      else if (status === 413) msg = 'File too large (max 25 MB)';
      else if (status === 502 || status === 503 || status === 504) msg = `Gateway error ${status} — retry in 30s (LLM provider may be rate-limiting)`;
      else if (status) msg = `Parse failed: ${status} ${typeof detail === 'string' ? detail : JSON.stringify(detail || '')}`;
      else if (err?.code === 'ERR_NETWORK') msg = 'Session likely expired — log out and log back in, then retry';
      else msg = `Network error: ${err?.message || 'unknown'}`;
      toast.error(msg);
      console.error('parse-schedule failed:', err);
    } finally {
      setUploadingSchedule(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const generateDraft = async (row) => {
    if (!row.company || !row.momText.trim()) {
      toast.error('Pick a company and paste minutes first');
      return;
    }
    if (!row.contact?.email) {
      toast.error('Pick a recipient email first (use the recipient row)');
      return;
    }
    updateRow(row.rowId, { status: 'generating', error: '' });
    try {
      const { data } = await axios.post(`${API}/follow-up/generate-draft`, {
        user_prompt: userPrompt,
        company: row.company,
        contact: row.contact,
        mom_text: row.momText,
      }, { withCredentials: true });
      updateRow(row.rowId, {
        status: 'drafted',
        draftSubject: data.subject,
        draftBody: data.body_html,
      });
    } catch (err) {
      updateRow(row.rowId, {
        status: 'error',
        error: err?.response?.data?.detail || err.message || 'Generation failed',
      });
    }
  };

  const generateAll = async () => {
    const ready = rows.filter(r => r.company && r.contact?.email && r.momText.trim() && r.status !== 'sent');
    if (ready.length === 0) {
      toast.error('Fill at least one row with company + contact + minutes');
      return;
    }
    toast.message(`Generating ${ready.length} drafts in parallel…`);
    await Promise.all(ready.map(r => generateDraft(r)));
  };

  const sendTest = async (row) => {
    if (!row.draftSubject || !row.draftBody) {
      toast.error('Generate a draft first');
      return;
    }
    updateRow(row.rowId, { status: 'testing', error: '' });
    try {
      const { data } = await axios.post(`${API}/follow-up/send-test`, {
        subject: row.draftSubject,
        body_html: row.draftBody,
        intended_to_email: row.contact?.email || '',
      }, { withCredentials: true });
      updateRow(row.rowId, { status: 'tested' });
      toast.success(`Test email sent to ${data.to}`);
    } catch (err) {
      updateRow(row.rowId, {
        status: 'error',
        error: err?.response?.data?.detail || err.message || 'Test send failed',
      });
    }
  };

  const sendOne = async (row) => {
    if (!row.draftSubject || !row.draftBody) return;
    if (!row.contact?.email) {
      toast.error('No recipient email for this row');
      return;
    }
    updateRow(row.rowId, { status: 'sending', error: '' });
    try {
      await axios.post(`${API}/follow-up/send`, {
        to_email: row.contact.email,
        cc_emails: row.ccEmails || [],
        subject: row.draftSubject,
        body_html: row.draftBody,
        company: row.company,
        contact: row.contact,
        mom_text: row.momText,
        routing_override: row.routingOverride,
      }, { withCredentials: true });
      updateRow(row.rowId, { status: 'sent' });
      toast.success(`Sent to ${row.contact.email}${row.ccEmails?.length ? ` + ${row.ccEmails.length} CC` : ''}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isRouting = detail && typeof detail === 'object' && detail.routing_violation;
      updateRow(row.rowId, {
        status: 'error',
        error: isRouting ? `Routing policy: ${detail.routing_violation}` : (detail || err.message || 'Send failed'),
      });
    }
  };

  const sendAll = async () => {
    const drafted = rows.filter(r => r.status === 'drafted' || r.status === 'tested');
    if (drafted.length === 0) {
      toast.error('No drafts ready to send');
      return;
    }
    if (!window.confirm(`Send ${drafted.length} follow-up emails via Gmail?`)) return;
    for (const r of drafted) {
      // sequential to keep Gmail quota happy
      // eslint-disable-next-line no-await-in-loop
      await sendOne(r);
    }
  };

  const sentCount = rows.filter(r => r.status === 'sent').length;
  const draftedCount = rows.filter(r => r.status === 'drafted' || r.status === 'tested').length;
  const testedCount = rows.filter(r => r.status === 'tested').length;
  const savedAtLabel = (() => {
    const s = loadState();
    if (!s?.savedAt) return null;
    const ago = Math.max(1, Math.round((Date.now() - s.savedAt) / 60000));
    return `auto-saved ${ago}m ago`;
  })();

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="followup-composer-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-violet-600" /> Conference Follow-up Composer
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Paste minutes-of-meeting per company, generate AI drafts in parallel,
            send a test to yourself, then fire to all recipients via Gmail.
            Schedule and drafts are auto-saved — refresh-safe.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">{sentCount} sent</Badge>
          <Badge variant="outline" className="border-violet-300 text-violet-700 bg-violet-50">{draftedCount} drafted</Badge>
          {testedCount > 0 && (
            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">{testedCount} tested</Badge>
          )}
          <Badge variant="outline">{rows.length} total</Badge>
          {savedAtLabel && (
            <span className="text-[11px] text-slate-400" data-testid="autosave-label">{savedAtLabel}</span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="text-slate-500 hover:text-rose-600"
            data-testid="followup-clear-all"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>
      </div>

      {/* Schedule upload card */}
      <Card className="border-violet-200 bg-violet-50/40">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-violet-700" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <p className="text-sm font-semibold text-slate-900">Upload conference meeting schedule (PDF or Excel)</p>
              <p className="text-xs text-slate-600 mt-0.5">
                Works with any partnering export (PartneringONE, BIO One-on-One, JPM,
                BIO-Europe, etc.). Auto-extracts company, contact, designation, and email
                per meeting and matches each row against your CRM.
              </p>
              {uploadSummary && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="upload-summary">
                  <Badge className="bg-white border border-slate-200 text-slate-700 text-[10px]">
                    {uploadSummary.fileName} · {uploadSummary.total} meetings
                  </Badge>
                  {uploadSummary.counts.exact_email > 0 && (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                      {uploadSummary.counts.exact_email} exact contact
                    </Badge>
                  )}
                  {uploadSummary.counts.domain > 0 && (
                    <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-[10px]">
                      {uploadSummary.counts.domain} domain
                    </Badge>
                  )}
                  {uploadSummary.counts.fuzzy > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                      {uploadSummary.counts.fuzzy} fuzzy name
                    </Badge>
                  )}
                  {uploadSummary.counts.unmatched > 0 && (
                    <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">
                      {uploadSummary.counts.unmatched} new (not in CRM)
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf,.xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="hidden"
              onChange={(e) => handleScheduleUpload(e.target.files?.[0])}
              data-testid="schedule-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingSchedule}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="upload-schedule-btn"
            >
              {uploadingSchedule
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Parsing…</>
                : <><Upload className="w-4 h-4 mr-1.5" /> Upload PDF / Excel</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tone & purpose prompt (applies to all drafts) */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Tone & purpose (applies to ALL drafts)
          </Label>
          <Textarea
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            rows={2}
            className="text-sm"
            placeholder="Tell Claude how to write all the emails…"
            data-testid="followup-global-prompt"
          />
        </CardContent>
      </Card>

      {/* Per-meeting rows */}
      {rows.map((row, idx) => (
        <FollowUpRow
          key={row.rowId}
          row={row}
          idx={idx}
          totalRows={rows.length}
          updateRow={updateRow}
          removeRow={removeRow}
          generateDraft={generateDraft}
          sendOne={sendOne}
          sendTest={sendTest}
        />
      ))}

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button variant="outline" onClick={addRow} data-testid="followup-add-row">
          <Plus className="w-4 h-4 mr-1" /> Add another meeting
        </Button>
        <div className="flex-1" />
        <Button
          variant="outline"
          className="border-violet-300 text-violet-700 hover:bg-violet-50"
          onClick={generateAll}
          disabled={rows.every(r => r.status === 'generating' || r.status === 'sent')}
          data-testid="followup-generate-all"
        >
          <Sparkles className="w-4 h-4 mr-1" /> Generate all drafts
        </Button>
        <Button
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={sendAll}
          disabled={draftedCount === 0}
          data-testid="followup-send-all"
        >
          <Send className="w-4 h-4 mr-1" /> Send {draftedCount > 0 ? `(${draftedCount})` : 'all'}
        </Button>
      </div>
    </div>
  );
}


function FollowUpRow({ row, idx, totalRows, updateRow, removeRow, generateDraft, sendOne, sendTest }) {
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [ccInput, setCcInput] = useState('');
  const debRef = useRef(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!searchQ || searchQ.length < 2) {
      setSearchResults([]);
      return;
    }
    debRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/follow-up/search-companies`, {
          params: { q: searchQ }, withCredentials: true,
        });
        setSearchResults(data.results || []);
      } catch { /* silent */ }
    }, 250);
  }, [searchQ]);

  const pickCompany = (c) => {
    updateRow(row.rowId, {
      company: c,
      contact: (c.contacts || [])[0] || null,
    });
    setSearchOpen(false);
    setSearchQ('');
    setSearchResults([]);
  };

  const onContactChange = (val) => {
    if (val === '__custom__') return;
    const found = (row.company?.contacts || []).find(c => (c.email || '') === val);
    updateRow(row.rowId, { contact: found || null });
  };

  const addCc = () => {
    const e = ccInput.trim();
    if (!e || !e.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    if ((row.ccEmails || []).includes(e)) {
      setCcInput('');
      return;
    }
    updateRow(row.rowId, { ccEmails: [...(row.ccEmails || []), e] });
    setCcInput('');
  };
  const removeCc = (e) =>
    updateRow(row.rowId, { ccEmails: (row.ccEmails || []).filter(x => x !== e) });

  // Display name: contact name if present, else company name (per user's spec —
  // never show a bare email as the headline).
  const displayName = (row.contact?.name && row.contact.name.trim())
    || row.company?.company_name
    || 'Unnamed recipient';

  const recipientEmail = row.contact?.email || '';

  const statusBadge = {
    idle: null,
    generating: <Badge className="bg-violet-100 text-violet-700"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating…</Badge>,
    drafted: <Badge className="bg-violet-100 text-violet-700">Drafted</Badge>,
    testing: <Badge className="bg-amber-100 text-amber-700"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing…</Badge>,
    tested: <Badge className="bg-amber-100 text-amber-700"><FlaskConical className="w-3 h-3 mr-1" /> Test sent</Badge>,
    sending: <Badge className="bg-sky-100 text-sky-700"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</Badge>,
    sent: <Badge className="bg-emerald-100 text-emerald-700"><Check className="w-3 h-3 mr-1" /> Sent</Badge>,
    error: <Badge className="bg-rose-100 text-rose-700"><AlertTriangle className="w-3 h-3 mr-1" /> Error</Badge>,
  }[row.status];

  const matchBadge = (() => {
    const s = row.meetingMeta?.matchStatus;
    if (!s) return null;
    if (s === 'exact_email') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"><Check className="w-2.5 h-2.5 mr-1" /> Matched contact</Badge>;
    if (s === 'domain') return <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-[10px]">Matched (domain)</Badge>;
    if (s.startsWith('fuzzy')) return <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Fuzzy match</Badge>;
    if (s === 'unmatched') return <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">New (not in CRM)</Badge>;
    return null;
  })();

  return (
    <Card data-testid={`followup-row-${idx}`}>
      <CardContent className="p-4 space-y-3">
        {(row.meetingMeta?.time || row.meetingMeta?.location || matchBadge) && (
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500" data-testid={`followup-row-${idx}-meta`}>
            {row.meetingMeta?.time && <span>📅 {row.meetingMeta.time}</span>}
            {row.meetingMeta?.location && <span>📍 {row.meetingMeta.location}</span>}
            {matchBadge}
          </div>
        )}

        {/* Header: company + status */}
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-xs text-slate-400 font-mono">#{idx + 1}</span>
            {row.company ? (
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span className="font-semibold text-slate-900 truncate">{row.company.company_name}</span>
                <Badge variant="outline" className="text-[10px]">{row.company.company_type}</Badge>
                {row.company.country && <span className="text-xs text-slate-500">· {row.company.country}</span>}
                <button
                  onClick={() => updateRow(row.rowId, { company: null, contact: null })}
                  className="text-xs text-slate-400 hover:text-rose-600 ml-1"
                  data-testid={`followup-row-${idx}-clear-company`}
                >
                  change
                </button>
              </div>
            ) : (
              <div className="relative w-full max-w-md">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  value={searchQ}
                  onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="Search company in CRM…"
                  className="pl-8 h-9 text-sm"
                  data-testid={`followup-row-${idx}-search`}
                />
                {searchOpen && searchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
                    {searchResults.map(r => (
                      <button
                        key={`${r.company_type}-${r.id}`}
                        onClick={() => pickCompany(r)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        data-testid={`followup-row-${idx}-pick-${r.id}`}
                      >
                        <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                          {r.company_name}
                          <Badge variant="outline" className="text-[10px]">{r.company_type}</Badge>
                        </div>
                        <div className="text-xs text-slate-500">{r.country} · {(r.contacts || []).length} contacts</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {statusBadge}
            {totalRows > 1 && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeRow(row.rowId)} data-testid={`followup-row-${idx}-remove`}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Recipient block */}
        {row.company && (
          <div className="border border-slate-200 rounded-md bg-slate-50/50 p-3 space-y-3">
            {/* Primary recipient: name on left, email on right */}
            <div className="grid sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-5">
                <Label className="text-xs text-slate-500">Recipient</Label>
                <Select
                  value={row.contact?.email || ''}
                  onValueChange={onContactChange}
                  disabled={!(row.company.contacts || []).filter(c => c.email).length}
                >
                  <SelectTrigger className="h-9 text-sm bg-white" data-testid={`followup-row-${idx}-contact-select`}>
                    <SelectValue placeholder={
                      (row.company.contacts || []).filter(c => c.email).length
                        ? 'Pick contact from CRM'
                        : 'No contacts on file — enter email manually →'
                    }>
                      <span className="font-medium text-slate-900 truncate">{displayName}</span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {(row.company.contacts || []).filter(c => c.email).map((c, i) => (
                      <SelectItem key={i} value={c.email}>
                        <span className="font-medium">{c.name || c.email}</span>
                        <span className="text-slate-400 ml-2">· {c.email}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!row.contact?.name && row.company && (
                  <p className="text-[10px] text-amber-700 mt-1">
                    No contact name — using company name. Edit contact in the company page if you want a real name in the email salutation.
                  </p>
                )}
              </div>
              <div className="sm:col-span-7">
                <Label className="text-xs text-slate-500">Email (editable)</Label>
                <Input
                  value={recipientEmail}
                  onChange={(e) => updateRow(row.rowId, {
                    contact: { ...(row.contact || {}), email: e.target.value, name: row.contact?.name || '' },
                  })}
                  className="h-9 text-sm bg-white font-mono"
                  placeholder="bd@company.com"
                  data-testid={`followup-row-${idx}-contact-email`}
                />
              </div>
            </div>

            {/* CC chips */}
            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1.5">CC <span className="text-slate-400 font-normal">(optional · add multiple)</span></Label>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {(row.ccEmails || []).map(e => (
                  <span
                    key={e}
                    className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
                    data-testid={`followup-row-${idx}-cc-chip-${e}`}
                  >
                    <span className="font-mono">{e}</span>
                    <button
                      onClick={() => removeCc(e)}
                      className="text-slate-400 hover:text-rose-600 rounded-full"
                      aria-label={`Remove CC ${e}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <div className="flex items-center gap-1">
                  <Input
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addCc();
                      }
                    }}
                    placeholder="cc@example.com (Enter to add)"
                    className="h-7 text-xs font-mono w-56 bg-white"
                    data-testid={`followup-row-${idx}-cc-input`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCc}
                    className="h-7 px-2"
                    data-testid={`followup-row-${idx}-cc-add`}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Minutes of meeting */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Minutes of meeting
          </Label>
          <Textarea
            value={row.momText}
            onChange={(e) => updateRow(row.rowId, { momText: e.target.value })}
            rows={4}
            className="text-sm font-mono"
            placeholder="Paste your raw meeting notes here. Bullet points are fine — the AI will synthesize."
            data-testid={`followup-row-${idx}-mom`}
          />
        </div>

        {/* Generated email — always visible once drafted */}
        {(row.draftSubject || row.draftBody) && (
          <div className="border-t border-slate-200 pt-3 space-y-3">
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Subject</Label>
                <Input
                  value={row.draftSubject}
                  onChange={(e) => updateRow(row.rowId, { draftSubject: e.target.value })}
                  className="h-9 text-sm"
                  data-testid={`followup-row-${idx}-subject`}
                />
              </div>
              <div className="pt-5">
                <SpamCheckChip
                  subject={row.draftSubject}
                  bodyHtml={row.draftBody}
                  onChange={(s, b) => updateRow(row.rowId, { draftSubject: s, draftBody: b })}
                  testId={`followup-row-${idx}-spamcheck`}
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Body (HTML — editable)</Label>
                <Textarea
                  value={row.draftBody}
                  onChange={(e) => updateRow(row.rowId, { draftBody: e.target.value })}
                  rows={14}
                  className="text-xs font-mono"
                  data-testid={`followup-row-${idx}-body`}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Preview (rendered)</Label>
                <div
                  className="border border-slate-200 rounded-md p-3 bg-white prose prose-sm max-w-none text-sm overflow-auto"
                  style={{ minHeight: 280, maxHeight: 360 }}
                  data-testid={`followup-row-${idx}-preview`}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.draftBody || '<em class="text-slate-400">Generate to see preview</em>') }}
                />
              </div>
            </div>
          </div>
        )}

        {row.error && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 break-words">
            {row.error}
            {row.error.toLowerCase().includes('routing') && (
              <label className="ml-2 inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={row.routingOverride}
                  onChange={(e) => updateRow(row.rowId, { routingOverride: e.target.checked })}
                  data-testid={`followup-row-${idx}-routing-override`}
                />
                Override routing policy & retry
              </label>
            )}
          </div>
        )}

        {/* Action row: Generate / Test / Send */}
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateDraft(row)}
            disabled={row.status === 'generating' || row.status === 'sending' || row.status === 'sent' || row.status === 'testing'}
            data-testid={`followup-row-${idx}-generate`}
          >
            {row.status === 'drafted' || row.status === 'tested' || row.status === 'error'
              ? <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Regenerate</>
              : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Generate draft</>}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => sendTest(row)}
            disabled={!row.draftSubject || !row.draftBody || row.status === 'sending' || row.status === 'sent' || row.status === 'testing' || row.status === 'generating'}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid={`followup-row-${idx}-send-test`}
          >
            <FlaskConical className="w-3.5 h-3.5 mr-1" /> Send test to me
          </Button>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => sendOne(row)}
            disabled={!row.draftSubject || !row.draftBody || row.status === 'sending' || row.status === 'sent' || row.status === 'generating' || row.status === 'testing'}
            data-testid={`followup-row-${idx}-send`}
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Send via Gmail
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
